import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2, Heading4 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { CopyIcon } from '@ui/icons/Copy.icon'

import { getDepositBankDetails } from '@utils/wallet.utils'

import { PaymentProviderRedirectKeyValue } from '@model/MasterTransactionDto'
import type { FormItemDto } from '@model/WalletPaymentProviderParametersDto'

import { useCopy } from '@hooks/useCopy'

import { Box } from '@shared/components/Box'
import { Notice } from '@shared/components/Notice'

interface DepositBankDetailsProps {
  details: FormItemDto[]
  amountValue: string
  reference?: string | null
  onReferenceCopy?: () => void
  dataKeyValue?: PaymentProviderRedirectKeyValue[] | null
}

interface BankDetailFieldProps {
  label: string
  value: string
  onCopy?: () => void
}

const BankDetailField: React.FC<BankDetailFieldProps> = ({ label, value, onCopy }) => {
  const { copy } = useCopy()
  const { t } = useTranslation()

  const handleCopy = () => {
    copy(value?.toString(), t('Copied'))
    onCopy?.()
  }

  return (
    <Box flex align='center' gap='x32' justify='space-between'>
      <Box flex direction='column'>
        <Body1 color='text-med-em'>{label}</Body1>
        <Body1 color='text-high-em'>{value}</Body1>
      </Box>
      <IconButton.Ghost onClick={handleCopy} icon={CopyIcon} />
    </Box>
  )
}

export const DepositBankDetails: React.FC<DepositBankDetailsProps> = (props) => {
  const { details, amountValue, reference, onReferenceCopy, dataKeyValue } = props
  const { t } = useTranslation()
  const isPixCase = Boolean(dataKeyValue?.length)
  return (
    <Box flex direction='column' gap='x48' fullWidth>
      <Box flex direction='column' gap='x12'>
        <Heading4 align='center' medium>
          {isPixCase
            ? t('Wallet.Complete your deposit')
            : t('Wallet.Make a Bank Transfer using your Unique Reference')}
        </Heading4>
        <Body2 wrap='pretty' align='center' color='text-med-em'>
          {isPixCase ? (
            t('Wallet.Pix Deposit Description')
          ) : (
            <>
              {t('Wallet.Please use the information below to make a bank transfer')}
              {t('Wallet.Please note that third-party transfers are not accepted for funding')}
            </>
          )}
        </Body2>
      </Box>
      <Box flex direction='column' gap='x24' fullWidth>
        {reference && (
          <>
            <BankDetailField
              label={t('Wallet.Reference number')}
              value={reference}
              onCopy={onReferenceCopy}
            />
            {isPixCase &&
              dataKeyValue?.map((item) => {
                if (!item?.name || item.value === null || item.value === undefined) return null

                return (
                  <BankDetailField key={item.name} label={item.name} value={String(item.value)} />
                )
              })}
            {!isPixCase && (
              <Notice
                noIcon
                id='reference-notice'
                message={t(
                  'Wallet.Please make sure you include your unique reference when making your bank transfer'
                )}
                type='info'
              />
            )}
          </>
        )}

        {getDepositBankDetails(details).map((item) => (
          <BankDetailField key={item.field} label={item.name} value={item.value.toString()} />
        ))}
        <BankDetailField label={t('Wallet.Amount')} value={amountValue} />
      </Box>
    </Box>
  )
}
